import { AuthBox, AuthBoxContainer } from '@/components/AuthBox';
import { BodyText } from '@/components/BodyText';
import { RoundedButton } from '@/components/RoundedButton';
import { TitleText } from '@/components/TitleText';
import { useOauth2Redirect } from '@/hooks/useOauth2Redirect';
import { Box, CircularProgress, styled } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportSignInError } from '@/services/auth/reportSignInError';
import { GENERIC_AUTH_ERROR_MESSAGE, getLambdaErrorMessage } from './authErrorMessage';

const ButtonGroup = styled(Box)({
  display: 'flex',
  gap: '4px',
  justifyContent: 'center',
});

export const SPINNER_ID = 'loading-spinner';
export const IDENTITY_PROVIDER_ERROR_DIALOG_ID = 'identity-provider-error-dialog';
export const SIGN_OUT_BUTTON_ID = 'sign-out-button';
export const SIGN_IN_BUTTON_ID = 'sign-in-button';

export const Oauth2RedirectPage = () => {
  const navigate = useNavigate();
  const { isRedirecting, authError, cognitoAuth, logout, errorContent } = useOauth2Redirect();
  const lambdaErrorMessage = cognitoAuth.error && getLambdaErrorMessage(cognitoAuth.error.message);
  const unmappedErrorMessage = cognitoAuth.error && !lambdaErrorMessage ? cognitoAuth.error.message : undefined;

  useEffect(() => {
    if (unmappedErrorMessage !== undefined) {
      void reportSignInError('oauth2-redirect', unmappedErrorMessage);
    }
  }, [unmappedErrorMessage]);

  if (cognitoAuth.error) {
    return (
      <AuthBoxContainer data-testid={IDENTITY_PROVIDER_ERROR_DIALOG_ID}>
        <AuthBox>
          <TitleText>Something went wrong</TitleText>
          <BodyText textAlign='center'>{lambdaErrorMessage || GENERIC_AUTH_ERROR_MESSAGE}</BodyText>
          <ButtonGroup>
            <RoundedButton variant='outlined' data-testid={SIGN_OUT_BUTTON_ID} onClick={() => void logout()}>
              Back to Sign In
            </RoundedButton>
          </ButtonGroup>
        </AuthBox>
      </AuthBoxContainer>
    );
  }

  if (authError) {
    return (
      <AuthBoxContainer data-testid={IDENTITY_PROVIDER_ERROR_DIALOG_ID}>
        <AuthBox>
          <TitleText>{errorContent.title}</TitleText>
          <BodyText>{errorContent.message}</BodyText>
          <ButtonGroup>
            <RoundedButton variant='outlined' data-testid={SIGN_OUT_BUTTON_ID} onClick={() => void logout()}>
              Sign Out
            </RoundedButton>
          </ButtonGroup>
        </AuthBox>
      </AuthBoxContainer>
    );
  }

  if (cognitoAuth.isLoading || isRedirecting || (cognitoAuth.isAuthenticated && !isRedirecting)) {
    return (
      <AuthBoxContainer>
        <CircularProgress size={24} sx={{ color: 'white' }} data-testid={SPINNER_ID} />
      </AuthBoxContainer>
    );
  }

  return (
    <AuthBoxContainer>
      <AuthBox>
        <TitleText>Authentication Required</TitleText>
        <BodyText>Unable to complete authentication. Please try signing in again.</BodyText>
        <ButtonGroup>
          <RoundedButton variant='outlined' onClick={() => void logout()}>
            Sign Out
          </RoundedButton>
          <RoundedButton variant='contained' onClick={() => void navigate('/signin')} data-testid={SIGN_IN_BUTTON_ID}>
            Sign In
          </RoundedButton>
        </ButtonGroup>
      </AuthBox>
    </AuthBoxContainer>
  );
};
